import { useMemo } from 'react';
import { TractNRI } from 'components/nri-table/types';
import { useExcludeLtds, useShowExpiredPermits } from './hooks';
import { SlotSummaryTable, SlotSummary } from './slot-summary-table';
import { SlotSummaryRow, NriVerificationCounts } from './types';

interface Props {
  slotSummary: SlotSummaryRow[];
  slots: TractNRI[];
  isLoading: boolean;
  tableId: string;
  showHighlight?: boolean;
}

/**
 * Subtracts excluded counts from total counts when toggle is on.
 * Used for "Exclude LTDs" toggle to subtract LTD verification counts.
 * @param {NriVerificationCounts | undefined} total - The total verification counts
 * @param {NriVerificationCounts | undefined} excluded - The excluded verification counts
 * @return {NriVerificationCounts | undefined} The resulting counts after subtraction
 */
const subtractCounts = (
  total: NriVerificationCounts | undefined,
  excluded: NriVerificationCounts | undefined
): NriVerificationCounts | undefined => {
  if (!total) return undefined;
  if (!excluded) return total;

  return {
    total: Math.max(0, total.total - excluded.total),
    verified: Math.max(0, total.verified - excluded.verified),
    notStarted: Math.max(0, total.notStarted - excluded.notStarted),
    incorrect: Math.max(0, total.incorrect - excluded.incorrect),
  };
};

export const SlotAllocationsSummary = (props: Props) => {
  const { slotSummary, isLoading, tableId, showHighlight = false } = props;
  // Two independent toggles (INS-7119)
  const { excludeLtds, setExcludeLtds } = useExcludeLtds(tableId);
  const { showExpiredPermits, setShowExpiredPermits } =
    useShowExpiredPermits(tableId);

  // Use backend-provided verification counts
  // Main counts already exclude expired permit by default (API-level change)
  // Subtract LTD counts only when "Exclude LTDs" toggle is ON
  const enhancedSlotSummary: SlotSummary[] = useMemo(() => {
    return slotSummary.map((row) => {
      // Main counts exclude expired permit by default
      // When excludeLtds is ON, subtract LTD counts (excludedNriVerification)
      const nriCounts = excludeLtds
        ? subtractCounts(row.nriVerification, row.excludedNriVerification)
        : row.nriVerification;

      return {
        ...row,
        nri: nriCounts,
      };
    });
  }, [slotSummary, excludeLtds]);

  return (
    <SlotSummaryTable
      slotSummary={enhancedSlotSummary}
      isLoading={isLoading}
      displayMode="nris"
      tableId={tableId}
      showHighlight={showHighlight}
      excludeLtds={excludeLtds}
      onExcludeLtdsToggle={setExcludeLtds}
      showExpiredPermits={showExpiredPermits}
      onShowExpiredPermitsToggle={setShowExpiredPermits}
    />
  );
};
